import React from 'react';

import { Badge } from 'cpool-components/general/Badge';
import { COLORS } from 'cpool-components/types';
import { EMPTY_PLACEHOLDER, EUROPACE_PAYMENT_DATES } from 'cpool-constants';
import type { SchemaConditions } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';

import { createDataSnippetsString } from '../utils/createDataSnippetsString';
import { createResidualDebtInsuranceString } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/utils/createResidualDebtInsuranceString.ts';
import { currencyFormat, dateFormat, timeFormat } from '~/lib/i18n.ts';

type Props = {
    offerCount: number;
    requestedAt: string;
    conditions: SchemaConditions;
    hasSecondApplicant: boolean;
};

export const SectionHeader: React.FC<Props> = ({ conditions, hasSecondApplicant, offerCount, requestedAt }) => {
    const { t } = useTranslation();
    const {
        commissionRequestInPercent,
        dataSnippets,
        financingPurpose,
        paymentDate,
        requestAmount,
        residualDebtInsurance,
        runtimeInMonths,
        totalFinancedAmount,
    } = conditions;
    const date = dateFormat(new Date(requestedAt));
    const time = timeFormat(new Date(requestedAt));
    const requestDate = t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.REQUEST_DATE_TIME', { date, time });
    const residualDebtInsuranceString = createResidualDebtInsuranceString(
        residualDebtInsurance ?? {},
        hasSecondApplicant,
        t,
    );
    const residualDataSnippetsString = createDataSnippetsString(dataSnippets ?? {}, hasSecondApplicant, t);

    return (
        <span className="flex items-center gap-3 text-left text-xs text-midnight">
            <Badge color={COLORS.WHITE} className="shrink-0 whitespace-nowrap">
                {requestDate}
            </Badge>
            <span className="flex flex-col">
                <span>
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.OFFER_COUNT', { count: offerCount })} |{' '}
                    {financingPurpose ? t(`FUNDING_TYPES.${financingPurpose}`) : EMPTY_PLACEHOLDER} -{' '}
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.REQUEST_AMOUNT', {
                        requestAmount: requestAmount != null ? currencyFormat(requestAmount) : EMPTY_PLACEHOLDER,
                    })}{' '}
                    (
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.DEBT_RESCHEDULING', {
                        amount:
                            typeof totalFinancedAmount === 'number'
                                ? currencyFormat(totalFinancedAmount)
                                : EMPTY_PLACEHOLDER,
                    })}{' '}
                    /{' '}
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.FRESH_MONEY', {
                        amount:
                            requestAmount && typeof totalFinancedAmount === 'number'
                                ? currencyFormat(requestAmount - totalFinancedAmount)
                                : EMPTY_PLACEHOLDER,
                    })}
                    ) -{' '}
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.LOAN_RUNTIME', {
                        runtimeInMonths: runtimeInMonths ?? EMPTY_PLACEHOLDER,
                    })}{' '}
                    -{' '}
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.OFFER_PROVISION', {
                        provision: commissionRequestInPercent ?? EMPTY_PLACEHOLDER,
                    })}{' '}
                    - {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.RESIDUAL_DEBT_INSURANCE')}{' '}
                    {residualDebtInsuranceString} -{' '}
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.INSTALLMENT_COLLECTION', {
                        installmentCollection: paymentDate === EUROPACE_PAYMENT_DATES.MONATSENDE ? '28.' : '15.',
                    })}
                </span>
                <span>
                    {t('BANK_CREDIT_REQUEST.HISTORY.SECTION_HEADER.DATA_SHARING')} {residualDataSnippetsString}
                </span>
            </span>
        </span>
    );
};
